/**
 * Maxlive88 — Realtime WebSocket hook + Announcement popup.
 *
 * useRealtime(role, token) opens a WS to /api/ws/{role} and yields:
 *   { connected, lastEvent, events }   (events = last 30 in memory)
 *
 * <AnnouncementPopup /> polls /api/aff-panel/announcements/pending-popup
 * and shows the next undismissed popup, returning a centered modal with the
 * CTA + dismiss action.
 */
import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export function useRealtime(role /* 'aff' | 'admin' */, token) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    if (!token || !role) return undefined;
    let isMounted = true;
    const connect = () => {
      if (!isMounted) return;
      try {
        const wsProto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
        const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
        const url = `${wsProto}://${wsHost}/api/ws/${role}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          if (!isMounted) return;
          setConnected(true);
          heartbeatRef.current = setInterval(() => {
            try { ws.send("ping"); } catch (_) { /* ignore */ }
          }, 25000);
        };
        ws.onmessage = (e) => {
          if (!isMounted) return;
          let payload;
          try { payload = JSON.parse(e.data); } catch { return; }
          if (!payload || !payload.kind || payload.kind === "ping" || payload.kind === "hello") return;
          setLastEvent(payload);
          setEvents((prev) => [payload, ...prev].slice(0, 30));
        };
        ws.onerror = () => {
          if (!isMounted) return;
          setConnected(false);
        };
        ws.onclose = () => {
          if (!isMounted) return;
          setConnected(false);
          if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
          // Auto-reconnect with backoff
          retryRef.current = setTimeout(connect, 5000);
        };
      } catch (_e) {
        retryRef.current = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      isMounted = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (retryRef.current) clearTimeout(retryRef.current);
      try { wsRef.current && wsRef.current.close(); } catch (_) { /* ignore */ }
    };
  }, [role, token]);

  const clear = useCallback(() => setEvents([]), []);
  return { connected, lastEvent, events, clear };
}

export function AnnouncementPopup({ api }) {
  const [ann, setAnn] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchPopup = useCallback(async () => {
    try {
      const r = await api.get("/aff-panel/announcements/pending-popup");
      const a = r.data?.announcement;
      if (a) { setAnn(a); setOpen(true); }
    } catch (_) { /* ignore */ }
  }, [api]);

  useEffect(() => {
    fetchPopup();
    const id = setInterval(fetchPopup, 60000);
    return () => clearInterval(id);
  }, [fetchPopup]);

  const dismiss = async () => {
    if (!ann) return;
    try { await api.post(`/aff-panel/announcements/${ann.id}/dismiss`); } catch (_) { /* ignore */ }
    setOpen(false);
    setAnn(null);
  };

  if (!open || !ann) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(2,3,7,0.78)", backdropFilter: "blur(8px)" }}
      data-testid="announcement-popup-overlay"
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6"
        style={{
          background: "linear-gradient(180deg, #14101e 0%, #0a070f 100%)",
          border: "1px solid rgba(212, 175, 55, 0.45)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(34, 192, 115, 0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.32em] text-emerald-200 font-bold mb-2">
          📣 Announcement
        </div>
        <h3 className="text-2xl font-black text-white mb-3" style={{ color: "#d4af37" }}>
          {ann.title}
        </h3>
        <div className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-y-auto">
          {ann.body}
        </div>
        <div className="mt-5 flex items-center gap-3 justify-end">
          {ann.cta_label && ann.cta_url ? (
            <a
              href={ann.cta_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center h-10 px-5 rounded-xl text-[13px] font-bold"
              style={{ background: "linear-gradient(135deg,#22c073,#16a34a)", color: "#0a070f" }}
              data-testid="announcement-popup-cta"
            >
              {ann.cta_label}
            </a>
          ) : null}
          <button
            onClick={dismiss}
            className="inline-flex items-center h-10 px-5 rounded-xl text-[13px] font-bold"
            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.12)" }}
            data-testid="announcement-popup-dismiss"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export function LiveFeed({ events }) {
  if (!events || events.length === 0) {
    return (
      <div className="p-glass-light p-5 text-center text-white/55 text-[13px]" data-testid="live-feed-empty">
        Waiting for live activity…
      </div>
    );
  }
  return (
    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="live-feed">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg"
             style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="px-2 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-[0.2em]"
               style={{ ..._badgeStyle(e.kind) }}>
            {e.kind}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] text-white truncate">
              {_describe(e)}
            </div>
            <div className="text-[10px] text-white/40 mt-0.5">{(e.ts || "").slice(11, 19)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function _describe(e) {
  const d = e.data || {};
  switch (e.kind) {
    case "register":      return `New register: ${d.username || ""} via ${d.promo_code || "—"}`;
    case "ftd":           return `FTD ৳${(d.amount || 0).toLocaleString()} by ${d.username || ""}`;
    case "deposit":       return `Deposit ৳${(d.amount || 0).toLocaleString()} by ${d.username || ""}`;
    case "withdraw":      return `Withdraw ৳${(d.amount || 0).toLocaleString()} by ${d.username || ""}`;
    case "commission":    return `Commission ৳${(d.amount || 0).toLocaleString()} credited (${d.period_start} → ${d.period_end})`;
    case "sub_commission":return `Sub-affiliate override ৳${(d.amount || 0).toLocaleString()}`;
    case "click":         return `Promo click on ${d.promo_code || ""}`;
    case "ticket_new":    return `New ticket: ${d.subject || ""} (${d.username || ""})`;
    case "ticket_reply":  return `Ticket reply: ${d.subject || ""}`;
    case "ticket_admin_reply": return `Admin replied: ${d.subject || ""}`;
    case "announcement":  return `Announcement: ${d.title || ""}`;
    default:              return `${e.kind}`;
  }
}

function _badgeStyle(kind) {
  const map = {
    register:     { color: "#a7f3d0", background: "rgba(34,192,115,0.12)", border: "1px solid rgba(34,192,115,0.4)" },
    ftd:          { color: "#fde68a", background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.4)" },
    deposit:      { color: "#7dd3fc", background: "rgba(56,189,248,0.12)", border: "1px solid rgba(56,189,248,0.4)" },
    withdraw:     { color: "#fca5a5", background: "rgba(229,75,89,0.12)",  border: "1px solid rgba(229,75,89,0.4)" },
    commission:   { color: "#fde68a", background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.4)" },
    sub_commission:{color:"#c4b5fd",  background: "rgba(167,139,250,0.12)",border: "1px solid rgba(167,139,250,0.4)" },
    click:        { color: "#cbd5e1", background: "rgba(148,163,184,0.10)",border: "1px solid rgba(148,163,184,0.3)" },
    ticket_new:   { color: "#fca5a5", background: "rgba(229,75,89,0.12)",  border: "1px solid rgba(229,75,89,0.4)" },
    ticket_reply: { color: "#7dd3fc", background: "rgba(56,189,248,0.12)", border: "1px solid rgba(56,189,248,0.4)" },
    ticket_admin_reply: { color: "#a7f3d0", background: "rgba(34,192,115,0.12)", border: "1px solid rgba(34,192,115,0.4)" },
    announcement: { color: "#fde68a", background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.4)" },
  };
  return map[kind] || { color: "#cbd5e1", background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.25)" };
}

export default useRealtime;
